import * as CL from '@design-system/component-library';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '../components/Modal/Modal';
import { ModalButtons } from '../components/Modal/ModalButtons';
import { useAuth } from '../public/site/AuthProvider';
import { setDisplaySessionExpiredNotification } from '../selfservice/common/localStorageUtils';
import { paths } from './constants/pathVariables';
import { logOutMsg, t } from './i18n';

const USER_ACTION_EVENTS = ['click', 'mousemove', 'scroll', 'keypress'];
const MODAL_MAX_DURATION = 5 * 60 * 1000;
const INACTIVITY_UNTIL_MODAL_SHOWN = 25 * 60 * 1000;

export interface SessionEndingModalProps {
  isEmployee: boolean;
}

export const SessionEndingModal = ({ isEmployee }: SessionEndingModalProps) => {
  const { logout } = useAuth();
  const [isModalOpen, setModalOpen] = useState(false);
  const lastActionTimestampRef = useRef(Date.now());
  const idleTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const updateLastActionTimestamp = () => (lastActionTimestampRef.current = Date.now());

  const hasUserBeenInactiveForTooLong = () =>
    Date.now() - lastActionTimestampRef.current > INACTIVITY_UNTIL_MODAL_SHOWN;

  const sessionLogout = useCallback(() => {
    clearTimeout(idleTimeoutRef.current);
    setModalOpen(false);
    logout(isEmployee ? paths.EMPLOYEE_LOGOUT : paths.SELF_SERVICE_LOGOUT);
  }, [isEmployee, logout]);

  const expireSession = useCallback(() => {
    setDisplaySessionExpiredNotification();
    clearTimeout(idleTimeoutRef.current);
    updateLastActionTimestamp(); // Prevents the modal from reappearing if logout takes long
    setModalOpen(false);
    logout(isEmployee ? paths.EMPLOYEE_SESSION_EXPIRED : paths.SELF_SERVICE_SESSION_EXPIRED);
  }, [isEmployee, logout]);

  const continueSession = () => {
    clearTimeout(idleTimeoutRef.current);
    setModalOpen(false);
  };

  useEffect(() => {
    USER_ACTION_EVENTS.forEach(event => window.addEventListener(event, updateLastActionTimestamp));
    return () => {
      USER_ACTION_EVENTS.forEach(event => window.removeEventListener(event, updateLastActionTimestamp));
    };
  });

  useEffect(() => {
    if (isModalOpen) {
      return;
    }
    const interval = setInterval(() => {
      if (hasUserBeenInactiveForTooLong()) {
        setModalOpen(true);
        idleTimeoutRef.current = setTimeout(expireSession, MODAL_MAX_DURATION);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [expireSession, isModalOpen]);

  return isModalOpen ? (
    <Modal
      closeable
      heading={
        isEmployee
          ? t.YF1F('Do you want to continue using OmaElisa for Employees?')
          : t.TTO8('Do you want to continue using OmaElisa for Companies?')
      }
      onClose={continueSession}
    >
      <p>{t.F223('Your session is about to expire and you will be automatically logged out in five minutes.')}</p>
      <ModalButtons>
        <CL.Button color="link" key="btnCancel" onClick={sessionLogout}>
          {t.LQ3X(logOutMsg)}
        </CL.Button>
        <CL.Button color="primary" key="btnConfirm" onClick={continueSession}>
          {t.TMK7('Continue using')}
        </CL.Button>
      </ModalButtons>
    </Modal>
  ) : null;
};
